import { scallopBadgeGeometry } from "@scroll/shared-utils";
import { resolveBadgeAccent, pickInk } from "@scroll/shared-colors";
import { useTheme } from "@/lib/theme";
import { cn } from "@/lib/utils";

const HEIGHTS = { sm: 14, md: 16, lg: 19, xl: 25 } as const;

export interface CreatorBadgeProps {
  verified?: boolean;
  accentColor?: string | null;
  size?: keyof typeof HEIGHTS;
  /** On photo/video overlays: force white seal + dark ink instead of theming onto media. */
  onMedia?: boolean;
  className?: string;
}

export default function CreatorBadge({
  verified = false, accentColor, size = "md", onMedia = false, className,
}: CreatorBadgeProps) {
  const { resolvedTheme } = useTheme();
  if (!verified) return null;

  const height = HEIGHTS[size];
  const g = scallopBadgeGeometry(height);
  const accent = onMedia ? null : resolveBadgeAccent(accentColor, resolvedTheme === "dark");

  let fill: string, ink: string;
  if (onMedia) { fill = "#FFFFFF"; ink = "#1D1916"; }
  else if (accent) { fill = accent; ink = pickInk(accent); }
  else { fill = "var(--scroll-text)"; ink = "var(--scroll-bg)"; }

  return (
    <svg
      width={g.width}
      height={g.height}
      viewBox={`0 0 ${g.width} ${g.height}`}
      role="img"
      aria-label="Verified"
      className={cn("inline-block shrink-0 align-middle", className)}
    >
      <path d={g.outline} fill={fill} />
      <path
        d={g.check.d}
        fill="none"
        stroke={ink}
        strokeWidth={g.check.strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
